import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { JobFormFields } from 'parent-portal/forms/JobRequestTypes';
import { FieldStates } from '../../FormTypes';
import FormField from '../../FormField';
import JobFormPage from '../JobFormPage';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { Checkbox, Colors, Text } from 'library';
import { Grid, styled } from '@mui/material';
import JobDescriptionInput from '../HeadlineDescriptionPage/JobDescriptionInput';
import { useUserContext } from 'UserContext';
import { getOhioRecommendations } from './OhioRecommendations';
import { getFloridaRecommendations } from './FloridaRecommendations';
import { getIllinoisRecommendations } from './IllinoisRecommendations';

export default function BusinessJobSummary({ fieldStates }: { fieldStates: FieldStates<JobFormFields> }) {
    const { user } = useUserContext();
    const { submit } = useJobFormContext();

    const [showDetails, setShowDetails] = useState(false);
    const [recommendations, setRecommendations] = useState<string[]>([]);
    const [ignoreRecommendations, setIgnoreRecommendations] = useState(false);
    const payScales = fieldStates.payScales.value;
    const minPay = fieldStates.pay.value.payMin;

    useEffect(() => {
        setRecommendations(getPayScaleRecomendations());
    }, []);

    function getPayScaleRecomendations() {
        const address = user?.address[0];
        const state = address?.state;

        if (!state) {
            return [];
        }

        const minRates = payScales.filter((x) => x.rate == minPay);
        if (state.toLowerCase() === 'ohio') {
            return getOhioRecommendations(minRates);
        } else if (state.toLowerCase() === 'florida') {
            return getFloridaRecommendations(minRates);
        } else if (state.toLowerCase() === 'illinois') {
            return getIllinoisRecommendations(minRates);
        }

        return [];
    }

    function recommendationSection() {
        if (!recommendations.length) {
            return;
        }

        return (
            <RecommendationsContainer>
                <RecommendationsTitleContainer container direction="row">
                    <Text variant="body1">
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            style={{
                                width: 20,
                                height: 20,
                                background: Colors.white,
                                borderRadius: 30,
                                marginRight: 5,
                            }}
                            color={Colors.error}
                        />
                        There is a low likelihood that Tandem can fill this job. This is due to the{' '}
                        <b>base pay and qualifications</b>.
                    </Text>
                </RecommendationsTitleContainer>
                <ul>
                    {recommendations.map((x) => (
                        <li key={x}>
                            <Text variant="body2">{x}</Text>
                        </li>
                    ))}
                </ul>
                <Checkbox
                    label={'Ignore recommendations and submit'}
                    checked={ignoreRecommendations}
                    onChange={setIgnoreRecommendations}
                />
            </RecommendationsContainer>
        );
    }

    function inlineText(text: string, boldText?: string) {
        return (
            <Text inline variant="body2">
                {text}
                {boldText ? (
                    <Text inline bold variant="body2">
                        {boldText}
                    </Text>
                ) : null}
                .{' '}
            </Text>
        );
    }

    const summaryPieces: { [key: string]: string | JSX.Element | null } = {
        headline: inlineText('This job is for ', fieldStates.headline.value),
        responsibilites: inlineText('The job responsibilities are: ', fieldStates.comments.value),
        pay: inlineText(
            'The pay rate is: $',
            fieldStates.pay.value.payMin === fieldStates.pay.value.payMax
                ? `${fieldStates.pay.value.payMin}/hr`
                : `${fieldStates.pay.value.payMin}-${fieldStates.pay.value.payMax}/hr`,
        ),
    };

    return (
        <JobFormPage
            title="Did we miss anything?"
            nextDisabled={recommendations.length > 0 && !ignoreRecommendations}
            onNextClicked={submit}
            nextText="Submit"
        >
            <Text textStyle={{ marginTop: 10 }}>
                After reviewing the details below,{' '}
                <Text bold inline>
                    click &quot;Submit&quot;
                </Text>{' '}
                to post your job!
            </Text>
            {recommendationSection()}
            <FormField title="Job Details" style={{ marginTop: 30 }}>
                <Text variant="body2" textStyle={{ marginTop: 10 }}>
                    {Object.keys(summaryPieces).map((k: string) => summaryPieces[k])}
                </Text>
            </FormField>
            <Grid style={{ marginTop: 20 }}>
                <Text
                    variant="body2"
                    bold
                    textStyle={{ textDecorationLine: 'underline', marginBottom: showDetails ? 20 : 0 }}
                    onClick={() => setShowDetails(!showDetails)}
                >
                    Add Other Details
                </Text>
                {showDetails ? <JobDescriptionInput business description={fieldStates.comments} /> : null}
            </Grid>
        </JobFormPage>
    );
}

const RecommendationsContainer = styled(Grid)({
    marginTop: 10,
});

const RecommendationsTitleContainer = styled(Grid)({
    marginBottom: 5,
});
